import { FACTSET_SCOPES } from "../shared/factset/factsetConstants";
import {
  getFactSetOAuthSession,
  type FactSetOAuthSessionData,
} from "../shared/factset/factsetOAuthClient";
import { forceReconnectFactSetOAuth } from "../shared/factset/factsetReconnect";

export type ChartCreatorMode = "api" | "legacy";

export interface ChartCreatorModeResolution {
  mode: ChartCreatorMode;
  session?: FactSetOAuthSessionData;
  message?: string;
  /**
   * True only for permanent OAuth-unavailable states (standalone SSO /
   * unsupported mode / not configured). Callers may cache these; transient
   * read failures are intentionally left uncached so a later request retries.
   */
  unsupported?: boolean;
}

export interface ResolveChartCreatorModeOptions {
  /** Optional logger for run logs. The tool path passes none so it stays silent. */
  log?: (message: string) => void;
  /** Invoked once a reconnect actually grants office-tools, so callers can drop legacy-keyed caches. */
  onReauthGranted?: () => void;
}

let reauthAttempted = false;
let reauthPromise: Promise<void> | null = null;

function sessionHasOfficeTools(session: FactSetOAuthSessionData): boolean {
  return (
    session.hasOfficeToolsAccess === true ||
    (session.scopes ?? []).includes(FACTSET_SCOPES.OFFICE_TOOLS)
  );
}

/**
 * Run a single FactSet reconnect, or join the one already in flight.
 */
export function runOrJoinChartCreatorReauth(): Promise<void> {
  if (!reauthPromise) {
    reauthAttempted = true;
    reauthPromise = forceReconnectFactSetOAuth().finally(() => {
      reauthPromise = null;
    });
  }
  return reauthPromise;
}

/**
 * Resolve whether Chart Creator should use the OAuth API or fall back to
 * the legacy host intent bridge, attempting at most one reconnect per
 * session to acquire the office-tools scope.
 */
export async function resolveChartCreatorMode(
  options: ResolveChartCreatorModeOptions = {},
): Promise<ChartCreatorModeResolution> {
  const { log, onReauthGranted } = options;
  try {
    let session = await getFactSetOAuthSession();
    if (!session.connected) {
      log?.("FactSet OAuth is not connected; using legacy chart intents.");
      return { mode: "legacy", session };
    }

    let hasOfficeToolsScope = sessionHasOfficeTools(session);
    if (hasOfficeToolsScope) {
      log?.("Chart Creator API available via FactSet OAuth.");
      return { mode: "api", session };
    }

    if (!reauthAttempted || reauthPromise) {
      try {
        log?.("Reconnecting FactSet to grant Chart Creator access...");
        await runOrJoinChartCreatorReauth();
        session = await getFactSetOAuthSession();
        hasOfficeToolsScope = sessionHasOfficeTools(session);
        if (hasOfficeToolsScope) {
          onReauthGranted?.();
          log?.("FactSet reconnected with Chart Creator access.");
          return { mode: "api", session };
        }
        log?.("FactSet reconnected, but Chart Creator scope is still missing; using legacy chart intents.");
        return { mode: "legacy", session };
      } catch (reauthError) {
        const message = reauthError instanceof Error ? reauthError.message : String(reauthError);
        log?.(`FactSet Chart Creator reconnect did not complete; using legacy chart intents: ${message}`);
        return { mode: "legacy", session };
      }
    }

    log?.("FactSet OAuth is connected without Chart Creator scope; using legacy chart intents.");
    return { mode: "legacy", session };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const unsupported =
      message.includes("standalone SSO") ||
      message.includes("unsupported_mode") ||
      message.includes("not configured");
    if (unsupported) {
      log?.(`Chart Creator OAuth unavailable (${message}); using legacy chart intents.`);
      return { mode: "legacy", message, unsupported: true };
    }
    log?.(`Could not read FactSet OAuth session for Chart Creator; using legacy chart intents: ${message}`);
    return { mode: "legacy", message };
  }
}
